import React, { useState, useEffect, useRef } from "react";
import { Info, X } from "lucide-react";
import { useHistory } from "react-router-dom";
import Tooltip from "../addentities/Tooltip";
import apiHelper from '../../apiHelper';
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal";

const validateGoogleMapLink = (link) => {
    if (!link) return true; // optional field

    const GOOGLE_LINK_REGEX =
        /^https:\/\/(?:([A-Za-z0-9-]+\.)*google\.[A-Za-z.]+|maps\.app\.goo\.gl)\/.+$/i;

    return GOOGLE_LINK_REGEX.test(link);
};

export default function AddWorkPlaces() {
    const history = useHistory();

    const [locationType, setLocationType] = useState("Virtual");
    const [activeTab, setActiveTab] = useState("Add Workplaces");
    const [disabled, setDisabled] = useState(false);
    const [validationError, setValidationError] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');

    const [formData, setFormData] = useState({
        workplace_name: '',
        workplace_short_name: '',
        status: '1',
        location_type: '',
        notes: '',
        virtual_link: '',
        address: {
            building_name: '',
            unit_number: '',
            street_address: '',
            street_address_2: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            google_map_link: ''
        }
    });

    const [errors, setFormErrors] = useState({
        workplace_name: '',
        virtual_link: '',
        street_address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        google_map_link: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Handle address fields
        if ([
            "building_name",
            "unit_number",
            "street_address",
            "street_address_2",
            "city",
            "state",
            "postal_code",
            "country",
            "google_map_link"
        ].includes(name)) {
            setFormData((prevData) => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    [name]: value
                }
            }));
        } else {
            // Handle normal fields
            setFormData((prevData) => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const discardRef = useRef(null);

    const handleDiscard = () => {
        setDiscardModalOpen(true);
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            history.goBack();
        }
        setDiscardModalOpen(false);
    };

    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [discardModalOpen]);

    const handleSave = async () => {
        setDisabled(true);
        let form_errors = {
            workplace_name: '',
            virtual_link: '',
            street_address: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            google_map_link: ''
        };
        let is_error = false;

        // Validate workplace_name (required for both Virtual and Onsite)
        if (!formData.workplace_name) {
            form_errors.workplace_name = "This field is required!";
            is_error = true;
        }

        // Conditional validation based on locationType
        if (locationType === "Virtual") {
            // For Virtual locations, NO fields are required from Location Info
            // Clear all location-related errors since they're not required for Virtual
            form_errors.virtual_link = '';
            form_errors.street_address = '';
            form_errors.city = '';
            form_errors.state = '';
            form_errors.postal_code = '';
            form_errors.country = '';
            form_errors.google_map_link = '';
        } else if (locationType === "Onsite") {
            // For Onsite locations, address fields are required
            if (!formData.address.street_address) {
                form_errors.street_address = "This field is required!";
                is_error = true;
            }
            if (!formData.address.city) {
                form_errors.city = "This field is required!";
                is_error = true;
            }
            if (!formData.address.state) {
                form_errors.state = "This field is required!";
                is_error = true;
            }
            if (!formData.address.postal_code) {
                form_errors.postal_code = "This field is required!";
                is_error = true;
            }
            if (!formData.address.country) {
                form_errors.country = "This field is required!";
                is_error = true;
            }
            
            // Validate Google Maps link if provided
            if (formData.address.google_map_link && !validateGoogleMapLink(formData.address.google_map_link)) {
                form_errors.google_map_link = "Google link is invalid";
                is_error = true;
            }
            
            // For Onsite locations, clear virtual_link error since it's not required
            form_errors.virtual_link = '';
        }

        setFormErrors(form_errors);

        if (is_error) {
            setDisabled(false);
            return false;
        }

        // Prepare payload based on location type
        let payloadData = {
            workplace_name: formData.workplace_name,
            status: formData.status,
            location_type: locationType,
            notes: formData.notes || '',
            virtual_link: formData.virtual_link || ''
        };

        // Handle workplace_short_name - send null if empty for Virtual locations
        if (locationType === "Virtual") {
            payloadData.workplace_short_name = formData.workplace_short_name || null;
        } else {
            payloadData.workplace_short_name = formData.workplace_short_name || null;
        }

        // Handle address based on location type
        if (locationType === "Virtual") {
            // For Virtual locations, don't send address at all or send null
            // Based on the backend code, we should not include address field for Virtual
            // The serializer has required=False for address
        } else if (locationType === "Onsite") {
            // For Onsite locations, send the complete address object
            payloadData.address = {
                building_name: formData.address.building_name || '',
                unit_number: formData.address.unit_number || '',
                street_address: formData.address.street_address,
                street_address_2: formData.address.street_address_2 || '',
                city: formData.address.city,
                state: formData.address.state,
                postal_code: formData.address.postal_code,
                country: formData.address.country,
                google_map_link: formData.address.google_map_link || ''
            };
        }

        console.log('Final Payload:', payloadData);

        const response = await apiHelper('entities/workplaces-create/', 'POST', payloadData);
        setDisabled(false);

        if (response) {
            if (response.ok) {
                setSuccessMessage('Workplace created successfully.');
                setValidationError([]);
                // Reset form
                setFormData({
                    workplace_name: '',
                    workplace_short_name: '',
                    status: '1',
                    location_type: '',
                    notes: '',
                    virtual_link: '',
                    address: {
                        building_name: '',
                        unit_number: '',
                        street_address: '',
                        street_address_2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: '',
                        google_map_link: ''
                    }
                });
                history.push("/company", { 
                    tab: "workplaces", 
                    success: "Workplace created successfully." 
                });
            } else if (response.status === 400) {
                const response_errors = await response.json();
                let errors = [];
                for (let i in response_errors) {
                    errors.push(`${i}: ${response_errors[i][0]}`);
                }
                setValidationError(errors);
                setSuccessMessage('');
            }
        }
    };

    const breadcrumbs = [
        "Organisations",
        "Company",
        "Workplaces",
        activeTab
    ];

    return (
        <div className="company-body main-company-body-inner">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Workplaces" ? "clickable" : ""}`}
                            onClick={() => {
                                if (crumb === "Workplaces") {
                                    history.push("/company", { tab: "workplaces" });
                                }
                            }}
                            style={{ cursor: crumb === "Workplaces" ? "pointer" : "default" }}
                        >
                            {crumb}
                            {!isLast ? "  " : ""}
                        </span>
                    );
                })}
            </div>

            {/* Error Banner */}
            {validationError.length > 0 && (
                <div
                    className="d-flex align-items-center justify-content-between"
                    role="alert"
                    style={{
                        boxShadow: "0px 4px 10px 0px #0000001F",
                        padding: "15px 10px",
                        borderRadius: "12px",
                        border: "1px solid #DC3545",
                        backgroundColor: "#F8D7DA",
                        color: "#842029",
                        fontSize: "12px",
                        position: "fixed",
                        top: "10px",
                        width: "calc(100% - 20px)",
                        maxWidth: "640px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2000,
                    }}
                >
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                        <strong>Failed to create workplace</strong>
                    </div>

                    <X
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => setValidationError([])}
                    />
                </div>
            )}

            <div className="edit-organisation-info">
                <div className="edit-organisation-left">
                    <h1>Add Workplace</h1>
                </div>
                <div className="discard-save-organisation-right d-flex align-items-center gap-2">
                    <button
                        className="dark-button"
                        type="button"
                        onClick={handleDiscard}
                    >
                        Discard
                    </button>
                    <DiscardConfirmationModal
                        isOpen={discardModalOpen}
                        onCancel={handleCancelDiscard}
                        onConfirm={handleDiscardConfirm}
                        entityName="Workplace"
                    />
                    <button className="dark-border" type="button" onClick={handleSave} disabled={disabled}>
                        Save
                    </button>
                </div>
            </div>

            <div className="info-section">
                {/* Workplace Info Card */}
                <div className="info-card new-info-overflow-auto">
                    <div className="info-header">
                        <h4 className="info-title">Workplace Info</h4>
                    </div>

                    <div className="info-content d-block">
                        <div className="grid-info-adding">
                            {successMessage && <div style={{ color: 'green' }} className="success-message">{successMessage}</div>}

                            <ul style={{ color: 'red' }}>
                                {validationError && validationError.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>

                            <div className="row gappingcls-added">
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label htmlFor="workplacename" className="form-label">
                                            Workplace Name{" "}
                                            <span className="error-color">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control common-input-all ${errors.workplace_name ? 'err-color' : ''}`}
                                            id="workplacename"
                                            placeholder="Workplace Name"
                                            name="workplace_name"
                                            value={formData.workplace_name}
                                            onChange={handleChange}
                                        />
                                        {errors.workplace_name && <p style={{ color: 'red' }}>{errors.workplace_name}</p>}
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label htmlFor="status" className="form-label">
                                            Status <span className="error-color">*</span>
                                        </label>
                                        <div className="flexing-type-radio">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="status"
                                                    id="statusActive"
                                                    value="1"
                                                    checked={formData.status === '1'}
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="statusActive"
                                                >
                                                    Active
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="status"
                                                    id="statusInactive"
                                                    value="0"
                                                    checked={formData.status === '0'}
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="statusInactive"
                                                >
                                                    Inactive
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row gappingcls-added">
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label
                                            htmlFor="shortName"
                                            className="form-label d-flex align-items-center gap-2"
                                        >
                                            Short Name
                                            <Tooltip content="A shorter, human-friendly label for your entity">
                                                <Info size={16} className="info-icon" />
                                            </Tooltip>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control common-input-all"
                                            id="shortName"
                                            placeholder="Short Name"
                                            name="workplace_short_name"
                                            value={formData.workplace_short_name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label
                                            htmlFor="locationType"
                                            className="form-label d-flex align-items-center gap-1"
                                        >
                                            Location Type <span className="error-color">*</span>
                                        </label>
                                        <div className="flexing-type-radio">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="locationType"
                                                    id="onsite"
                                                    value="Onsite"
                                                    checked={locationType === "Onsite"}
                                                    onChange={() => setLocationType("Onsite")}
                                                />
                                                <label className="form-check-label" htmlFor="onsite">
                                                    Onsite
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="locationType"
                                                    id="virtual"
                                                    value="Virtual"
                                                    checked={locationType === "Virtual"}
                                                    onChange={() => setLocationType("Virtual")}
                                                />
                                                <label className="form-check-label" htmlFor="virtual">
                                                    Virtual
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Location Info Card - Only show for Onsite locations */}
                {locationType === "Onsite" && (
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Location Info</h4>
                        </div>
                        <div className="info-content d-block">
                            <div className="grid-info-adding">
                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="addressLine1" className="form-label d-flex align-items-center gap-2">
                                                Address Line 1 <span className="error-color">*</span>
                                                <Tooltip content="Enter the physical address">
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.street_address ? 'err-color' : ''}`}
                                                id="addressLine1"
                                                placeholder="Address Line 1"
                                                name="street_address"
                                                value={formData.address.street_address}
                                                onChange={handleChange}
                                            />
                                            {errors.street_address && <p style={{ color: 'red' }}>{errors.street_address}</p>}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner spacing-top-side-all-head">
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                id="addressLine2"
                                                placeholder="Address Line 2 (Optional)"
                                                name="street_address_2"
                                                value={formData.address.street_address_2}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/*<div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="buildingName" className="form-label">
                                                Building Name
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                id="buildingName"
                                                placeholder="Building Name"
                                                name="building_name"
                                                value={formData.address.building_name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="unitNumber" className="form-label">
                                                Unit Number
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                id="unitNumber"
                                                placeholder="Unit Number"
                                                name="unit_number"
                                                value={formData.address.unit_number}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>*/}

                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="city" className="form-label">City <span className="error-color">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.city ? 'err-color' : ''}`}
                                                id="city"
                                                placeholder="City"
                                                name="city"
                                                value={formData.address.city}
                                                onChange={handleChange}
                                            />
                                            {errors.city && <p style={{ color: 'red' }}>{errors.city}</p>}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="state" className="form-label">State <span className="error-color">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.state ? 'err-color' : ''}`}
                                                id="state"
                                                placeholder="State"
                                                name="state"
                                                value={formData.address.state}
                                                onChange={handleChange}
                                            />
                                            {errors.state && <p style={{ color: 'red' }}>{errors.state}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="postalCode" className="form-label">Postal Code <span className="error-color">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.postal_code ? 'err-color' : ''}`}
                                                id="postalCode"
                                                placeholder="Postal Code"
                                                name="postal_code"
                                                value={formData.address.postal_code}
                                                onChange={handleChange}
                                            />
                                            {errors.postal_code && <p style={{ color: 'red' }}>{errors.postal_code}</p>}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="country" className="form-label">Country <span className="error-color">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.country ? 'err-color' : ''}`}
                                                id="country"
                                                placeholder="Country"
                                                name="country"
                                                value={formData.address.country}
                                                onChange={handleChange}
                                            />
                                            {errors.country && <p style={{ color: 'red' }}>{errors.country}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label htmlFor="googlelink" className="form-label d-flex align-items-center gap-2">
                                                Google Link
                                                <Tooltip content="A map link or location URL">
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control common-input-all ${errors.google_map_link ? 'err-color' : ''}`}
                                                id="googlelink"
                                                placeholder="Google Maps URL"
                                                name="google_map_link"
                                                value={formData.address.google_map_link}
                                                onChange={handleChange}
                                            />
                                            {errors.google_map_link && <p style={{ color: 'red' }}>{errors.google_map_link}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Additional Info (Note) */}
                <div className="info-card">
                    <div className="info-header">
                        <h4 className="info-title">Additional Info</h4>
                    </div>
                    <div className="info-content">
                        <div className="info-inner common-textarea">
                            <label
                                htmlFor="exampleFormControlTextarea1"
                                className="form-label"
                            >
                                Note
                            </label>
                            <textarea
                                className="form-control w-100 common-textarea-all"
                                id="exampleFormControlTextarea1"
                                rows="3"
                                placeholder="Any additional notes..."
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}